import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Badge,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../../api";
import {
  createReminder,
  updateReminder,
} from "../../acommsapi"; // adjust imports to your API helpers

/**
 * Props:
 * - smsDetail: (optional) existing reminder object, e.g.
 *     {
 *       _id: "6840401b563fe8c1b5058a3d",
 *       reminderType: "SMS",
 *       "1DayPracs": [...],
 *       sundayPracs: [...],
 *       "1DayText": "Please Pay",
 *       sundayText: "Please Pay"
 *     }
 * - onSmsUpdate: callback to reload parent data
 * - ...props: show, onHide, etc.
 */
function SmsModal({ smsDetail, onSmsUpdate = () => {}, ...props }) {
  const [user, setUser] = useState({});
  const [practices, setPractices] = useState([]); // [{ practiceNumber, practiceName }, ...]
  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);

  // State for selections & texts:
  const [oneDaySelected, setOneDaySelected] = useState([]); // array of practiceNumbers
  const [sundaySelected, setSundaySelected] = useState([]);
  const [oneDayText, setOneDayText] = useState("");
  const [sundayText, setSundayText] = useState("");

  // Available variables for drag and drop
  const availableVariables = [
    { label: "First Name", value: "${appointment.patientFirstName}" },
    { label: "Last Name", value: "${appointment.patientLastName}" },
    { label: "Date", value: "${appointment.date}" },
    { label: "Time", value: "${appointment.time}" },
    { label: "Practitioner", value: "${appointment.resourceName}" },
    { label: "Practice", value: "${appointment.practiceName}" },
    { label: "Location Name",     value: "${appointment.location.name}" },
  { label: "Address 1",         value: "${appointment.location.address1}" },
  { label: "Address 2",         value: "${appointment.location.address2}" },
  { label: "City",              value: "${appointment.location.city}" },
  { label: "Province/State",    value: "${appointment.location.province}" },
  { label: "Postal Code",       value: "${appointment.location.postalCode}" },
  ];

  useEffect(() => {
    // Decode user token
    const token = sessionStorage.getItem("user");
    if (token) {
      const decoded = jwtDecode(token);
      setUser(decoded);
      // Fetch all practices for this user
      getUserPracticeById(decoded._id).then((userPractices) => {
        setPractices(userPractices);
      });
    }

    // If editing, populate fields
    if (smsDetail) {
      setOneDaySelected(Array.isArray(smsDetail["1DayPracs"]) ? smsDetail["1DayPracs"] : []);
      setSundaySelected(Array.isArray(smsDetail.sundayPracs) ? smsDetail.sundayPracs : []);
      setOneDayText(smsDetail["1DayText"] || "");
      setSundayText(smsDetail.sundayText || "");
    } else {
      // Creating: clear
      setOneDaySelected([]);
      setSundaySelected([]);
      setOneDayText("");
      setSundayText("");
    }
  }, [smsDetail]);

  // Split array of practices into two roughly equal halves
  const splitInTwoCols = (arr) => {
    const half = Math.ceil(arr.length / 2);
    return [arr.slice(0, half), arr.slice(half)];
  };

  // Handle checkbox change for 1DayPracs
  const toggleOneDay = (num) => {
    setOneDaySelected((prev) =>
      prev.includes(num) ? prev.filter((p) => p !== num) : [...prev, num]
    );
  };

  // Handle checkbox change for sundayPracs
  const toggleSunday = (num) => {
    setSundaySelected((prev) =>
      prev.includes(num) ? prev.filter((p) => p !== num) : [...prev, num]
    );
  };

  // Handle drag start for variable pills
  const handleDragStart = (e, variable) => {
    e.dataTransfer.setData("text/plain", variable.value);
    e.dataTransfer.effectAllowed = "copy";
  };

  // Handle drop on textarea
  const handleDrop = (e, textType) => {
    e.preventDefault();
    const variable = e.dataTransfer.getData("text/plain");
    const textarea = e.target;
    const cursorPosition = textarea.selectionStart;
    const currentValue = textType === "oneDay" ? oneDayText : sundayText;
    
    const newValue = 
      currentValue.slice(0, cursorPosition) + 
      variable + 
      currentValue.slice(cursorPosition);
    
    if (textType === "oneDay") {
      setOneDayText(newValue);
    } else {
      setSundayText(newValue);
    }
  };

  // Handle drag over to allow drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  // Handle click to insert variable at cursor position
  const handleVariableClick = (variable, textType) => {
    const textarea = document.getElementById(textType === "oneDay" ? "oneDayText" : "sundayText");
    const cursorPosition = textarea.selectionStart || 0;
    const currentValue = textType === "oneDay" ? oneDayText : sundayText;
    
    const newValue = 
      currentValue.slice(0, cursorPosition) + 
      variable.value + 
      currentValue.slice(cursorPosition);
    
    if (textType === "oneDay") {
      setOneDayText(newValue);
    } else {
      setSundayText(newValue);
    }
    
    // Focus back to textarea and set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        cursorPosition + variable.value.length,
        cursorPosition + variable.value.length
      );
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      reminderType: "SMS",
      "1DayPracs": oneDaySelected,
      sundayPracs: sundaySelected,
      "1DayText": oneDayText,
      sundayText: sundayText,
    };

    try {
      if (smsDetail && smsDetail._id) {
        await updateReminder(smsDetail._id, payload);
      } else {
        await createReminder(payload);
      }

      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
        props.onHide();
        onSmsUpdate();
      }, 1000);
    } catch (error) {
      console.error("Error saving SMS reminder:", error);
    }
  };

  const handleClose = () => {
    setCloseAlert(true);
  };

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setCloseAlert(false);
    // Reset to original or defaults
    if (smsDetail) {
      setOneDaySelected(smsDetail["1DayPracs"] || []);
      setSundaySelected(smsDetail.sundayPracs || []);
      setOneDayText(smsDetail["1DayText"] || "");
      setSundayText(smsDetail.sundayText || "");
    } else {
      setOneDaySelected([]);
      setSundaySelected([]);
      setOneDayText("");
      setSundayText("");
    }
    props.onHide();
  };

  // Variable Pills Component
  const VariablePills = ({ textType }) => (
    <div className="mb-2">
      <small className="text-muted d-block mb-1">
        Drag variables into the message or click to insert at cursor:
      </small>
      <div className="d-flex flex-wrap gap-1">
        {availableVariables.map((variable, index) => (
          <Badge
            key={index}
            bg="primary"
            className="user-select-none"
            style={{ 
              cursor: "grab", 
              padding: "4px 8px",
              fontSize: "0.75rem"
            }}
            draggable
            onDragStart={(e) => handleDragStart(e, variable)}
            onClick={() => handleVariableClick(variable, textType)}
            title="Drag to message or click to insert"
          >
            {variable.label}
          </Badge>
        ))}
      </div>
    </div>
  );

  // Build lists of practice names & numbers for checkboxes
  // Use practices state which has objects { practiceNumber, practiceName }
  const practiceNums = practices.map((p) => p.practiceNumber);

  const [oneDayCol1, oneDayCol2] = splitInTwoCols(practiceNums);
  const [sundayCol1, sundayCol2] = splitInTwoCols(practiceNums);

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="sms-modal-title"
      centered
      onHide={handleClose}
    >
      <Modal.Header closeButton={!closeAlert && !successAlert}>
        <Modal.Title id="sms-modal-title" className="text-primary">
          {smsDetail ? "Edit SMS Reminder" : "Add SMS Reminder"}
        </Modal.Title>
      </Modal.Header>

      {closeAlert && (
        <Alert
          variant="info"
          className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
        >
          <span>Are you sure you want to close? Changes will not be saved.</span>
          <div className="ms-auto d-flex">
            <Button
              variant="outline-primary"
              className="me-1"
              onClick={handleCancelYes}
            >
              Yes
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleCancelNo}
            >
              No
            </Button>
          </div>
        </Alert>
      )}

      {successAlert && (
        <Alert
          variant="success"
          className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
        >
          <span>SMS reminder saved successfully!</span>
        </Alert>
      )}

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            {/* 1 Day Before Section */}
            <Col md={6}>
              <h6 className="text-primary mb-3">1 Day Before</h6>
              <Row>
                <Col xs={6}>
                  {oneDayCol1.map((num) => (
                    <Form.Check
                      key={num}
                      type="checkbox"
                      label={
                        practices.find((p) => p.practiceNumber === num)
                          ?.practiceName || num
                      }
                      checked={oneDaySelected.includes(num)}
                      onChange={() => toggleOneDay(num)}
                      className="mb-1"
                    />
                  ))}
                </Col>
                <Col xs={6}>
                  {oneDayCol2.map((num) => (
                    <Form.Check
                      key={num}
                      type="checkbox"
                      label={
                        practices.find((p) => p.practiceNumber === num)
                          ?.practiceName || num
                      }
                      checked={oneDaySelected.includes(num)}
                      onChange={() => toggleOneDay(num)}
                      className="mb-1"
                    />
                  ))}
                </Col>
              </Row>
              <Form.Group controlId="oneDayText" className="mt-3">
                <Form.Label><strong>Message:</strong></Form.Label>
                <VariablePills textType="oneDay" />
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter SMS text to send 1 day before. Drag variables from above or click them to insert."
                  value={oneDayText}
                  onChange={(e) => setOneDayText(e.target.value)}
                  onDrop={(e) => handleDrop(e, "oneDay")}
                  onDragOver={handleDragOver}
                  required={oneDaySelected.length > 0}
                  style={{ 
                    minHeight: "80px",
                    border: "2px dashed #dee2e6",
                    transition: "border-color 0.2s ease"
                  }}
                  onDragEnter={(e) => {
                    e.target.style.borderColor = "#0d6efd";
                    e.target.style.backgroundColor = "#f8f9ff";
                  }}
                  onDragLeave={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.backgroundColor = "";
                  }}
                />
              </Form.Group>
            </Col>

            {/* Divider */}
            <Col xs={12} className="my-3">
              <hr />
            </Col>

            {/* Sunday Section */}
            <Col md={6}>
              <h6 className="text-primary mb-3">On Sunday for the Week</h6>
              <Row>
                <Col xs={6}>
                  {sundayCol1.map((num) => (
                    <Form.Check
                      key={num}
                      type="checkbox"
                      label={
                        practices.find((p) => p.practiceNumber === num)
                          ?.practiceName || num
                      }
                      checked={sundaySelected.includes(num)}
                      onChange={() => toggleSunday(num)}
                      className="mb-1"
                    />
                  ))}
                </Col>
                <Col xs={6}>
                  {sundayCol2.map((num) => (
                    <Form.Check
                      key={num}
                      type="checkbox"
                      label={
                        practices.find((p) => p.practiceNumber === num)
                          ?.practiceName || num
                      }
                      checked={sundaySelected.includes(num)}
                      onChange={() => toggleSunday(num)}
                      className="mb-1"
                    />
                  ))}
                </Col>
              </Row>
              <Form.Group controlId="sundayText" className="mt-3">
                <Form.Label><strong>Message:</strong></Form.Label>
                <VariablePills textType="sunday" />
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter SMS text to send on Sunday. Drag variables from above or click them to insert."
                  value={sundayText}
                  onChange={(e) => setSundayText(e.target.value)}
                  onDrop={(e) => handleDrop(e, "sunday")}
                  onDragOver={handleDragOver}
                  required={sundaySelected.length > 0}
                  style={{ 
                    minHeight: "80px",
                    border: "2px dashed #dee2e6",
                    transition: "border-color 0.2s ease"
                  }}
                  onDragEnter={(e) => {
                    e.target.style.borderColor = "#0d6efd";
                    e.target.style.backgroundColor = "#f8f9ff";
                  }}
                  onDragLeave={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.backgroundColor = "";
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-center align-items-center w-100">
          <Button
            variant="primary"
            className="px-5 py-2"
            type="submit"
            onClick={handleSubmit}
          >
            {smsDetail ? "Save Changes" : "Submit"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default SmsModal;